import { useState } from "react";
import {
  CMMD_PRIORITY_ORDER,
  normalizeCmmdPriority,
  type CmmdBoardTask,
} from "@cmmd-forge/contracts";
import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from "~/components/data-views/DataTable";
import { getStatusConfig } from "~/components/command-center/statusConfig";
import { taskStatusDescriptor } from "~/components/projects/boardColumns";
import { cn } from "~/lib/utils";
import type { ProjectTaskSort } from "~/views/viewDefinition";
import {
  activeTasksInOrder,
  EMPTY_PROJECT_MILESTONES,
  EMPTY_TASK_CONVERSATION_IDS,
  type ProjectTaskViewProps,
} from "./taskViewTypes";
import { ProjectTaskTableRow } from "./ProjectTaskTableRow";
import { ProjectTaskMobileList } from "./ProjectTaskMobileList";
import {
  milestoneIdFromGroupKey,
  taskMilestoneGroupResolver,
  taskMilestoneGroups,
} from "./taskMilestoneGroups";
import { workflowGroupForTask } from "./taskWorkflowGroups";
import { canQuickCreateInWorkflowGroup } from "./projectTaskCreateOptions";
import { taskAssigneeName } from "./taskPresentation";

// ── Columns (sortable + resizable) ─────────────────────────────────────────

const TASK_COLUMNS: ReadonlyArray<DataTableColumn> = [
  {
    id: "title",
    header: "Issue",
    defaultWidth: 420,
    fill: true,
    resizable: false,
    sortable: true,
  },
  // Wide enough for the assignee chip plus two agent badges and a +N pill
  // without wrapping — the cluster is the widest thing this column renders.
  { id: "assignee", header: "Assignees", defaultWidth: 112, resizable: false, sortable: true },
  { id: "owner", header: "Owner", defaultWidth: 64, resizable: false, sortable: true },
  { id: "due", header: "Due", defaultWidth: 96, sortable: true },
];

// v4: the assignee column widened for the multi-assignee cluster and an Owner
// column was inserted, so persisted v3 widths would misalign every row.
const COLUMN_WIDTH_STORAGE_KEY = "forge:cmmd.tasks:list-column-widths:v4";

// ── Sort comparison (surface owns it; DataTable owns the sort STATE + UI) ────

type TaskComparator = (a: CmmdBoardTask, b: CmmdBoardTask) => number;

const TASK_COLUMN_COMPARATORS: Readonly<Record<string, TaskComparator>> = {
  title: (a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
  priority: (a, b) => taskPriorityRank(a) - taskPriorityRank(b),
  due: (a, b) => (a.dueDate ?? "~").localeCompare(b.dueDate ?? "~"),
  assignee: (a, b) =>
    (taskAssigneeName(a) ?? "~").localeCompare(taskAssigneeName(b) ?? "~", undefined, {
      sensitivity: "base",
    }),
  // "~" sorts unowned last under every locale collation we ship.
  owner: (a, b) =>
    (a.ownerName ?? "~").localeCompare(b.ownerName ?? "~", undefined, { sensitivity: "base" }),
};

/** Compare tasks by what the selected column presents. */
function compareTasksByColumn(a: CmmdBoardTask, b: CmmdBoardTask, columnId: string): number {
  return TASK_COLUMN_COMPARATORS[columnId]?.(a, b) ?? 0;
}

function taskPriorityRank(task: CmmdBoardTask): number {
  const normalized = normalizeCmmdPriority(task.priority);
  const index = normalized === null ? -1 : CMMD_PRIORITY_ORDER.indexOf(normalized);
  return index === -1 ? CMMD_PRIORITY_ORDER.length : index;
}

function normalizeTaskListSort(sort: DataTableSort | null): ProjectTaskSort | null {
  if (sort === null) return null;
  if (
    sort.columnId !== "title" &&
    sort.columnId !== "assignee" &&
    sort.columnId !== "owner" &&
    sort.columnId !== "due"
  ) {
    return null;
  }
  return { columnId: sort.columnId, direction: sort.direction };
}

function statusGroupLeading(statusKey: string) {
  const config = getStatusConfig(statusKey);
  return (
    <span
      className={cn("inline-flex size-3 shrink-0 rounded-full border-2", config.circleClass)}
      aria-hidden="true"
    />
  );
}

/**
 * Linear-style task list: a sortable/resizable table grouped by status, built
 * on the shared DataTable (a status-grouped card list on narrow viewports).
 * Rows are rank-ordered by default (F2); a header click switches to that
 * column's sort. Archived rows are excluded.
 */
export function ProjectTaskListView(props: ProjectTaskViewProps) {
  const {
    tasks,
    threadIndex,
    pendingTaskIds,
    conversationTaskIds = EMPTY_TASK_CONVERSATION_IDS,
    onOpenTask,
    onOpenThread,
    workItemPrefix,
    onCreateInGroup,
    onCreateInLane,
    backlogView = false,
    groupBy = "status",
    milestones = EMPTY_PROJECT_MILESTONES,
    taskSort,
    onTaskSortChange,
  } = props;

  // DataTable owns the sort STATE + header UI; this view owns the COMPARISON
  // (and the rank-ordered default when no column is active).
  const [fallbackSort, setFallbackSort] = useState<ProjectTaskSort | null>(null);
  const sort = taskSort === undefined ? fallbackSort : taskSort;
  const setSort = (nextSort: DataTableSort | null) => {
    const normalized = normalizeTaskListSort(nextSort);
    if (onTaskSortChange != null) onTaskSortChange(normalized);
    else setFallbackSort(normalized);
  };

  const active = activeTasksInOrder(tasks);
  const milestoneGroupForTask = taskMilestoneGroupResolver(taskMilestoneGroups(milestones));
  const rows =
    sort === null
      ? active
      : active.toSorted((a, b) => {
          const factor = sort.direction === "asc" ? 1 : -1;
          return factor * compareTasksByColumn(a, b, sort.columnId);
        });
  const backlogStage = props.workflow?.stages.find((stage) => stage.category === "backlog");

  return (
    <div className="flex min-h-0 flex-col overflow-hidden bg-background">
      <div className="flex min-h-0 flex-col sm:overflow-hidden">
        <DataTable
          columns={TASK_COLUMNS}
          rows={rows}
          renderRow={(task) => (
            <ProjectTaskTableRow
              key={task.id}
              task={task}
              threadIndex={threadIndex}
              pending={pendingTaskIds.has(task.id)}
              hasConversation={conversationTaskIds.has(task.id)}
              onOpenTask={onOpenTask}
              onOpenThread={onOpenThread}
              workItemPrefix={workItemPrefix}
            />
          )}
          widthsStorageKey={COLUMN_WIDTH_STORAGE_KEY}
          sort={sort}
          onSortChange={setSort}
          groupOf={(task) => {
            if (groupBy === "milestone") return milestoneGroupForTask(task);
            return props.workflow
              ? workflowGroupForTask(task, props.workflow)
              : (() => {
                  const descriptor = taskStatusDescriptor(task);
                  return { ...descriptor, leading: statusGroupLeading(descriptor.key) };
                })();
          }}
          groupCatalog={
            backlogView && groupBy === "milestone" ? taskMilestoneGroups(milestones) : undefined
          }
          onCreateInGroup={
            backlogView && groupBy === "milestone" && onCreateInLane != null
              ? (group) =>
                  onCreateInLane({
                    status: "backlog",
                    milestoneId: milestoneIdFromGroupKey(group.key),
                    ...(backlogStage != null ? { workflowStageId: backlogStage.id } : {}),
                  })
              : groupBy === "status" && onCreateInGroup != null
                ? (group) => onCreateInGroup(group.key)
                : undefined
          }
          canCreateInGroup={(group) =>
            backlogView && groupBy === "milestone"
              ? !(props.mutationDisabled ?? false)
              : canQuickCreateInWorkflowGroup(
                  props.workflow,
                  group.key,
                  props.mutationDisabled ?? false,
                )
          }
          createInGroupLabel={(group) => `Add task in ${group.label}`}
          emptyLabel="No tasks yet."
          mobileBody={<ProjectTaskMobileList {...props} tasks={rows} />}
          minWidthPx={560}
        />
      </div>
    </div>
  );
}
